/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, EventService, HttpService, LazyloadService, PermissionService, ToastService } from '@dm/core';
import { ProductConfig } from 'app/config';
import { PackageTypeE } from 'app/feature/settings/basic-infomation/upgrade/upgrade';
import { UpgradeService as UpgradeServiceInBasicInfo } from 'app/feature/settings/basic-infomation/upgrade/upgrade.service';
import { CmoQueryFrom, CmoQueryTypeE, CmoUpdSysModeE, CmoUpdSysStepTaskStatusE,
    CurrentStageE, Progress, UpgradeMethodE} from 'app/feature/share/header-settings/upgrade/upgrade.const';
import { UpgradeService } from 'app/feature/share/header-settings/upgrade/upgrade.service';
import { type Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, mergeMap } from 'rxjs/operators';

import { CheckToastComponent } from '../components/check-toast/check-toast.component';
import { type ChsCollector, type OtaUpgVersionInfo } from '../types/mo';
import { ToolService } from './tool.service';

/**
 * 获取新版本信息配置项
 *
 */
export interface GetPakageInfoOpt {
    /**
     * 预检查。先检查是否具备获取新版本信息的条件
     */
    precheck?: boolean;
    /**
     * 自动模式。登录后自动获取或者进入升级页面自动获取采用自动模式
     */
    isAuto?: boolean;
    /**
     * 弹报错信息
     */
    doException?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class OtaUpgradeService {
    /**
     * 升级版本信息
     */
    updateVersionInfo: OtaUpgVersionInfo[] = [];

    /**
     * 目标版本
     */
    targetVersion: OtaUpgVersionInfo;

    /**
     * 中间版本
     */
    transitionVersion: OtaUpgVersionInfo[];

    /**
     * 是否是登录进来的
     */
    isLogin = false;

    /**
     * 只有补丁包
     */
     onlyHotpatch = false;

    constructor(
        private httpService: HttpService,
        private permissionService: PermissionService,
        private lazyLoadService: LazyloadService,
        private toastService: ToastService,
        private toolService: ToolService,
        private upgradeService: UpgradeService,
        private eventService: EventService,
        private upgradeServiceInBasicInfo: UpgradeServiceInBasicInfo,
    ) {}

    init(): void {
        this.queryOtaUpgradeStep();
    }

    /**
     * 获取当前OTA升级步骤信息
     */
    queryOtaUpgradeStep(): void {
        // A800不支持升级
        if (ProductConfig.isHideUpgrade) {
            return;
        }

        // 高端release不支持DM升级
        if (!this.upgradeService.ifShowUpgrade()) {
            this.getUpgradeStatus(false);
            return;
        }
        this.queryStep({ doException: false}).subscribe(({ data }) => {
            // OTA升級方式
            if (data && Number(data.upgradeMethod) === UpgradeMethodE.OtaUpgrade) {
                const step = Number(data.currentStage);
                // 分发包
                if (step === CurrentStageE.DistributionPackage ||
                    step === CurrentStageE.PreCheck ||
                    step === CurrentStageE.AfterCheck ||
                    step === CurrentStageE.SystemUpgrade ||
                    step === CurrentStageE.HotpatchUpgrade) {
                    this.getOtaUpgVersionInfo(true, { doException: false })
                    .pipe(
                        finalize(() => {
                            this.eventService.emit('otaUpgradeReEnter', { type: step, onlyHotpatch: this.onlyHotpatch });
                        }),
                    )
                    .subscribe();
                } else {
                    this.getUpgradeStatus();
                }
            } else {
                this.getUpgradeStatus();
            }
        }, () => {
            this.getUpgradeStatus();
        });
    }

    /**
     * 获取当前升级状态
     */
    getUpgradeStatus(otaSystemUpgrade = true): void {
        // 热补丁检查，checkType为0是热补丁
        const hotpatchCheck$ = this.httpService.get(this.httpService.makeUrl('upgrade.checkProgress')
            .setParams({ checkType: 0 }), { doException: false })
            // 捕获错误，确保其他请求不受影响
            .pipe(catchError(() => of({ data: null })));

        // 热补丁升级
        const hotpatchUpgrade$ = this.httpService.get(this.httpService.makeUrl('upgrade.upgradeProgress')
            .setParams({ CMO_QUERY_TYPE: CmoQueryTypeE.Hotpatch, CMO_QUERY_FROM: CmoQueryFrom.DM }), { doException: false })
            // 捕获错误，确保其他请求不受影响
            .pipe(catchError(() => of({ data: null })));

        // 系统升级包含了快速升级和OTA升级
        const systemUpgrade$ = this.httpService.get(this.httpService.makeUrl('upgrade.upgradeProgress')
            .setParams({
                CMO_QUERY_TYPE: otaSystemUpgrade ? CmoQueryTypeE.SystemUpgrade : CmoQueryTypeE.Hotpatch,
                CMO_QUERY_FROM: CmoQueryFrom.DM,
            }),
            { doException: false })
            // 捕获错误，确保其他请求不受影响
            .pipe(catchError(() => of({ data: null })));

        forkJoin([hotpatchCheck$, hotpatchUpgrade$, systemUpgrade$]).subscribe(([hotpatchCheck, hotpatchUpgrade, systemUpgrade]) => {
            if (hotpatchCheck.data && Number(hotpatchCheck.data.checkProgress) !== 100) {
                // 热补丁校验中
                if (hotpatchCheck.data.curCheckType === 'HotpatchVerify') {
                    this.eventService.emit('inUpgradeProcess', { type: CmoUpdSysModeE.Hotpatch, process: Progress.verifying });
                    this.upgradeServiceInBasicInfo.inUpgradeProcess = true;
                    return;
                }
                // 热补丁预检查中
                if (hotpatchCheck.data.curCheckType === 'HotpatchPrecheck') {
                    this.eventService.emit('inUpgradeProcess', { type: CmoUpdSysModeE.Hotpatch, process: Progress.prechecking });
                    return;
                }
            }

            // 热补丁升级中。CMO_UPD_SYS_MODE为5是热补丁,进度不为100%则认为处于升级中
            if (hotpatchUpgrade.data && Number(hotpatchUpgrade.data.CMO_UPD_SYS_MODE) === 5 &&
                Number(hotpatchUpgrade.data.CMO_UPD_SYS_STEP_STEP_PERCENT) !== 100) {
                    this.eventService.emit('inUpgradeProcess', { type: CmoUpdSysModeE.Hotpatch, process: Progress.upgrading });
                    this.upgradeServiceInBasicInfo.inUpgradeProcess = true;
                    return;
            }

            if (!systemUpgrade.data) {
                return;
            }

            // 快速升级或者OTA升级
            const inFastOrOta = [
                CmoUpdSysModeE.FastUpgrade,
                CmoUpdSysModeE.OtaUpgrade,
            ].includes(Number(systemUpgrade.data.CMO_UPD_SYS_MODE));

            // 处于快速升级或者OTA升级的升级中、回滚中、升级成功、升级失败、升级暂停、回滚失败、回滚暂停中、回滚成功
            const inFastOrOtaProcess = [
                CmoUpdSysStepTaskStatusE.Upgrading,
                CmoUpdSysStepTaskStatusE.Rollbacking,
                CmoUpdSysStepTaskStatusE.UpgradeSuc,
                CmoUpdSysStepTaskStatusE.UpgradeFail,
                CmoUpdSysStepTaskStatusE.UpgradePause,
                CmoUpdSysStepTaskStatusE.RollbackFail,
                CmoUpdSysStepTaskStatusE.RollbackPause,
                CmoUpdSysStepTaskStatusE.RollbackSuc,
            ].includes(Number(systemUpgrade.data.CMO_UPD_SYS_STEP_TASK_STATUS));

            // 快速升级/OTA升级流程中
            if (inFastOrOta && inFastOrOtaProcess) {
                this.eventService.emit('inUpgradeProcess',
                    {
                        type: Number(systemUpgrade.data.CMO_UPD_SYS_MODE),
                        process: Number(systemUpgrade.data.CMO_UPD_SYS_STEP_TASK_STATUS),
                        otaSystemUpgrade,
                    });
                // 升级成功、失败、回滚成功不处于升级流程中，依旧要获取ota包信息
                if ([
                        CmoUpdSysStepTaskStatusE.UpgradeSuc,
                        CmoUpdSysStepTaskStatusE.UpgradeFail,
                        CmoUpdSysStepTaskStatusE.RollbackSuc,
                    ].includes(Number(systemUpgrade.data.CMO_UPD_SYS_STEP_TASK_STATUS))) {
                    this.getOtaPackages();
                } else {
                    this.upgradeServiceInBasicInfo.inUpgradeProcess = true;
                }
                return;
            }

            // 未处于升级流程中,获取ota包信息
            this.getOtaPackages();
        });
    }

    /**
     * 获取ota包信息
     */
    getOtaPackages(): void {
        // oem、高端release不支持ota升级
        // 登录进来的才获取OTA信息，刷新页面不获取
        if (this.toolService.isOem() || !this.isLogin) {
            return;
        }
        this.isLogin = false;
        // 获取OTA包信息
        this.getPackages({ precheck: true, isAuto: true, doException: false })
            .subscribe(() => {
                if (this.targetVersion) {
                    this.showToast();
                }
            });
    }

    /**
     * 获取全部包信息
     *
     * @param option 选项
     */
    getPackages(option: GetPakageInfoOpt = {}): Observable<OtaUpgVersionInfo[]> {
        const { precheck = true, isAuto = true, doException = true } = option;

        // 超级管理员，eService开关打开才能获取新版本信息
        if (this.permissionService.isSuperAdmin() || !precheck) {
            return this.getEServiceStatus(!precheck, { doException })
                .pipe(
                    map((data: ChsCollector) => data.switch === 'on'),
                    mergeMap((eServiceSwitch: boolean) => {
                        if (eServiceSwitch) {
                            return this.getOtaUpgVersionInfo(isAuto, { doException });
                        } else {
                            return of(null);
                        }
                    }),
                );
        } else {
            return of(null);
        }
    }

    showToast(): void {
        const component = this.lazyLoadService.createComponent(CheckToastComponent, {
            onClose: () => {
                this.toastService.close(msg);
                msg = null;
            },
        });
        let msg = this.toastService.info(component, { sticky: true });
    }

    /**
     * 获取eService状态
     *
     * @param ignore 跳过下发，直接认为eService返回了开关开启
     */
    getEServiceStatus(ignore = false, opt?: BaseRequestOptionsArgs): Observable<ChsCollector> {
        if (ignore) {
            return of({ switch: 'on' });
        }
        const rest = this.httpService.makeUrl('eService.getChsCollector');
        return this.httpService.get(rest, opt)
            .pipe(
                map(({ data }) => data),
            );
    }

    getOtaUpgVersionInfo(isAuto = true, opt?: BaseRequestOptionsArgs): Observable<OtaUpgVersionInfo[]> {
        const rest = this.httpService.makeUrl('upgrade.getOtaVersionInfo').setParams({ isAuto });

        return this.httpService.get(rest, opt)
            .pipe(
                map(({ data }) => {
                    // 更新各版本信息
                    this.getUpgradePackage(data);
                    return data;
                }),
            );
    }

    /**
     * 获取升级包信息
     */
    getUpgradePackage(data: OtaUpgVersionInfo[]): void {
        if (!data) {
            return;
        }
        this.updateVersionInfo = data;

        if (this.updateVersionInfo && this.updateVersionInfo.length === 1 &&
            Number(this.updateVersionInfo[0].packageType) === PackageTypeE.HOT_PATCH_PKG) {
                this.onlyHotpatch = true;
        } else {
            this.onlyHotpatch = false;
        }

        // 底层返回的包会按发布时间排序，最后一个是目标版本，前面的都是中间版本
        this.targetVersion = data.slice(-1)[0];
        this.transitionVersion = data.slice(0, -1);
    }

    // 退出OTA升级流程
    exitStep(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('upgrade.exitStep');
        return this.httpService.delete(rest, options);
    }

    // 查询OTA升级步骤信息
    queryStep(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('upgrade.queryStep');
        return this.httpService.get(rest, options);
    }
}
